class CartPage extends HTMLElement {
  constructor() {
    super();
    this.isUpdating = false;
    this.pendingUpdates = new Map();
    this.inputTimeouts = new Map();
    this.sectionId = this.dataset.sectionId || 'main-cart-items';
    this.onQuantityButton = this.onQuantityButton.bind(this);
    this.onQuantityInput = this.onQuantityInput.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.onDiscountApply = this.onDiscountApply.bind(this);
    this.onNoteInput = this.onNoteInput.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onQuantityButton);
    this.addEventListener('input', this.onQuantityInput);
    this.addEventListener('click', this.onRemove);
    this.addEventListener('click', this.onDiscountApply);
    this.addEventListener('input', this.onNoteInput);
    this.initRecommendations();
    this.initRecentlyViewed();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onQuantityButton);
    this.removeEventListener('input', this.onQuantityInput);
    this.removeEventListener('click', this.onRemove);
    this.removeEventListener('click', this.onDiscountApply);
    this.removeEventListener('input', this.onNoteInput);
  }

  onQuantityButton(event) {
    const button = event.target.closest('[data-cart-quantity-change]');
    if (!button) return;
    const key = button.dataset.key;
    const input = this.querySelector(`[data-cart-quantity-input][data-key="${key}"]`);
    if (!input) return;
    const action = button.dataset.action;
    const current = parseInt(input.value, 10) || 0;
    const next = action === 'increase' ? current + 1 : Math.max(0, current - 1);
    input.value = next;
    this.scheduleUpdate(key, next);
  }

  onQuantityInput(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;
    const key = input.dataset.key;
    if (this.inputTimeouts.has(key)) clearTimeout(this.inputTimeouts.get(key));
    this.inputTimeouts.set(
      key,
      setTimeout(() => {
        const next = parseInt(input.value, 10) || 0;
        this.scheduleUpdate(key, next);
      }, 300)
    );
  }

  onRemove(event) {
    const button = event.target.closest('[data-cart-remove]');
    if (!button) return;
    const item = this.querySelector(`[data-cart-item][data-key="${button.dataset.key}"]`);
    const remove = () => this.scheduleUpdate(button.dataset.key, 0);
    if (item && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      item.classList.add('is-removing');
      let fallbackId;
      const onEnd = () => { clearTimeout(fallbackId); remove(); };
      item.addEventListener('transitionend', onEnd, { once: true });
      fallbackId = setTimeout(remove, 400);
    } else {
      remove();
    }
  }

  onDiscountApply(event) {
    const button = event.target.closest('[data-cart-discount-apply]');
    if (!button) return;
    const input = this.querySelector('[data-cart-discount-input]');
    if (!input) return;
    const code = input.value.trim();
    if (!code) return;
    this.applyDiscount(code);
  }

  onNoteInput(event) {
    const textarea = event.target.closest('[data-cart-note]');
    if (!textarea) return;
    if (this.noteTimeout) clearTimeout(this.noteTimeout);
    this.noteTimeout = setTimeout(() => this.updateNote(textarea.value), 500);
  }

  scheduleUpdate(key, quantity) {
    this.pendingUpdates.set(key, quantity);
    if (this.isUpdating) return;
    this.processPendingUpdates();
  }

  processPendingUpdates() {
    if (this.pendingUpdates.size === 0 || this.isUpdating) return;
    const updates = Object.fromEntries(this.pendingUpdates);
    this.pendingUpdates.clear();
    this.updateCart(updates);
  }

  updateCart(updates) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.setLoading(true);

    const body = new URLSearchParams();
    body.set('sections', this.sectionId);
    Object.entries(updates).forEach(([key, quantity]) => {
      body.set(`updates[${key}]`, quantity);
    });

    return fetch('/cart/update.js', { method: 'POST', body })
      .then((response) => response.json())
      .then((data) => this.handleCartResponse(data))
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.isUpdating = false;
        this.processPendingUpdates();
      });
  }

  applyDiscount(code) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.setLoading(true);

    const body = new URLSearchParams();
    body.set('discount', code);
    body.set('sections', this.sectionId);

    return fetch('/cart/update.js', { method: 'POST', body })
      .then((response) => response.json())
      .then((data) => this.handleCartResponse(data))
      .catch((error) => this.handleError(error))
      .finally(() => {
        this.isUpdating = false;
        this.processPendingUpdates();
      });
  }

  updateNote(note) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    const body = new URLSearchParams();
    body.set('note', note);

    return fetch('/cart/update.js', { method: 'POST', body })
      .then((response) => response.json())
      .then((data) => {
        if (data.item_count !== undefined) this.updateCartCount(data);
      })
      .catch(() => {})
      .finally(() => {
        this.isUpdating = false;
      });
  }

  handleCartResponse(data) {
    if (!data) return;
    this.updateCartCount(data);
    if (data.sections && data.sections[this.sectionId]) {
      this.replaceSection(data.sections[this.sectionId]);
    } else if (data[this.sectionId]) {
      this.replaceSection(data[this.sectionId]);
    } else if (data.items && data.item_count !== undefined) {
      // Fallback: if items present but no section, we can re-render by fetching section.
      return this.refresh();
    }
    this.setLoading(false);
  }

  handleError(error) {
    this.setLoading(false);
    if (window.console && console.error) console.error('CartPage error:', error);
  }

  replaceSection(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newSection = parsed.querySelector('cart-page');
    if (newSection) {
      this.innerHTML = newSection.innerHTML;
      this.initRecommendations();
      this.initRecentlyViewed();
    }
  }

  refresh() {
    this.setLoading(true);
    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.sectionId);
    return fetch(url.toString())
      .then((response) => response.json())
      .then((data) => {
        if (data[this.sectionId]) {
          this.replaceSection(data[this.sectionId]);
        }
      })
      .catch(() => {})
      .finally(() => {
        this.setLoading(false);
      });
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
  }

  updateCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  }

  initRecommendations() {
    const container = this.querySelector('[data-cart-recommendations]');
    const grid = this.querySelector('[data-cart-recommendations-grid]');
    if (!container || !grid) return;

    const productIds = (container.dataset.productIds || '')
      .replace(/^\[|\]$/g, '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    if (productIds.length === 0) return;

    const limit = container.dataset.limit || 4;
    const intent = container.dataset.intent || 'related';
    const productId = productIds[0];
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    const url = `${root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;

    grid.innerHTML = '';

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const products = (data.products || []).filter(
          (product) => !productIds.includes(String(product.id))
        );
        if (products.length > 0) {
          grid.innerHTML = products.slice(0, limit).map((product) => this.renderProductCard(product, container.dataset)).join('');
          grid.hidden = false;
          const fallback = container.querySelector('[data-cart-recommendations-fallback]');
          if (fallback) fallback.hidden = true;
        } else {
          this.showRecommendationsFallback(container);
        }
      })
      .catch(() => this.showRecommendationsFallback(container));
  }

  showRecommendationsFallback(container) {
    const grid = container.querySelector('[data-cart-recommendations-grid]');
    const fallback = container.querySelector('[data-cart-recommendations-fallback]');
    if (grid) grid.hidden = true;
    if (fallback) fallback.hidden = false;
  }

  initRecentlyViewed() {
    const container = this.querySelector('[data-recently-viewed]');
    const grid = this.querySelector('[data-recently-viewed-grid]');
    if (!container || !grid) return;

    let products = [];
    try {
      const raw = localStorage.getItem('sebella_recently_viewed');
      products = raw ? JSON.parse(raw) : [];
    } catch (e) {}

    const limit = parseInt(container.dataset.limit || 4, 10);
    const inCart = Array.from(this.querySelectorAll('[data-cart-item]')).map((el) => parseInt(el.dataset.productId, 10));
    products = products.filter((product) => product && product.id && !inCart.includes(product.id)).slice(0, limit);

    if (products.length > 0) {
      container.hidden = false;
      grid.innerHTML = products.map((product) => this.renderProductCard(product, container.dataset)).join('');
    } else {
      container.hidden = true;
    }
  }

  renderProductCard(product, dataset) {
    const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
    let rawImage = product.featured_image || (product.images || [])[0] || '';
    if (rawImage && typeof rawImage === 'object') rawImage = rawImage.src || rawImage.url || rawImage.featured_image || '';
    const image = typeof rawImage === 'string' ? rawImage : '';
    const title = this.escapeHtml(product.title);
    const price = this.formatMoney(variant ? variant.price : product.price);
    const compare = variant && variant.compare_at_price ? `<s class="product-card__compare-price">${this.formatMoney(variant.compare_at_price)}</s>` : '';
    const quickAdd = dataset.quickAdd === 'true' && variant ? `<button type="button" class="product-card__quick-add" data-quick-add data-variant-id="${variant.id}" data-added-text="${window.cartStrings.addedText}">${window.cartStrings.addToCart}</button>` : '';
    let imageRatio = dataset.productCardImageRatio || '3 / 4';
    if (imageRatio === 'natural' || imageRatio === 'auto') imageRatio = '3 / 4';
    const imageFit = dataset.productCardImageFit || 'cover';

    return `
      <li class="product-grid__item product-card product-card--compact" data-product-id="${product.id}">
        <a href="${product.url}" class="product-card__media" style="--product-card-image-ratio: ${imageRatio}; --product-card-image-fit: ${imageFit};">
          ${image ? `<img src="${image}" alt="${title}" loading="lazy" width="200" height="250" class="product-card__image product-card__image--primary">` : ''}
        </a>
        <div class="product-card__info">
          <h3 class="product-card__title"><a href="${product.url}">${title}</a></h3>
          <div class="product-card__price">
            <span class="product-card__price-current">${price}</span>
            ${compare}
          </div>
          ${quickAdd}
        </div>
      </li>
    `;
  }

  formatMoney(cents) {
    if (window.formatMoney) return window.formatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency: Shopify.currency.active || 'USD' }).format(cents / 100);
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage);
}
